import React from 'react';

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { Box, Button, Divider, FormControl, FormLabel, MenuItem, Paper, Select, Stack } from '@mui/material';

import { FINANCE_CURRENCIES } from '~/consts/currencies';

import {
  getPackageBaseCurrency,
  getPackageCampaignCode,
  getPackageDisplayPrice,
  getPackageNumberOfNights,
  getPackageValue,
} from '../../utils';

import PackageIdentifier from './PackageIdentifier';
import PackageNumberField from './PackageNumberField';
import PackageTextField from './PackageTextField';

interface Props {
  offerPackage: App.Package;
  onChange: (nextPackageValues: Partial<App.Package> & Record<string, unknown>) => void;
  onAutofill: () => void;
}

export default function DetailsTab({ offerPackage, onAutofill, onChange }: Props) {
  const sourcePackageUuid = offerPackage.le_package_id;
  const sourceDealOptionSalesforceId = offerPackage.id_salesforce_external
    ? String(offerPackage.id_salesforce_external)
    : undefined;
  const baseCurrency = getPackageBaseCurrency(offerPackage);
  const currencyOptions = Array.from(new Set([baseCurrency, ...FINANCE_CURRENCIES]));

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          spacing={2}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2.5}
            divider={<Divider orientation="vertical" flexItem />}
          >
            <PackageIdentifier label="Source package UUID" value={sourcePackageUuid} />
            <PackageIdentifier label="Source deal option SF ID" value={sourceDealOptionSalesforceId} />
          </Stack>
          <Button size="small" startIcon={<AutoFixHighIcon />} onClick={onAutofill}>
            Autofill from deal option
          </Button>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: 3,
        }}
      >
        <PackageTextField
          label="Display name"
          value={offerPackage.deal_option_name || offerPackage.name || ''}
          onChange={(value) => onChange({ deal_option_name: value, name: value })}
        />
        <PackageTextField
          label="Campaign code"
          value={getPackageCampaignCode(offerPackage)}
          onChange={(value) => onChange({ campaign_code: value })}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
          gap: 3,
        }}
      >
        <Stack spacing={1}>
          <FormLabel>Base currency</FormLabel>
          <FormControl size="small" fullWidth>
            <Select value={baseCurrency} onChange={(event) => onChange({ cost_currency: event.target.value })}>
              {currencyOptions.map((currency) => (
                <MenuItem key={currency} value={currency}>
                  {currency}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <PackageNumberField
          label="Price"
          value={getPackageDisplayPrice(offerPackage)}
          onChange={(value) => onChange({ cost_price: value })}
        />
        <PackageNumberField
          label="Value"
          value={getPackageValue(offerPackage)}
          onChange={(value) => onChange({ value })}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
          gap: 3,
        }}
      >
        <PackageNumberField
          label="Number of nights"
          value={getPackageNumberOfNights(offerPackage)}
          onChange={(value) => onChange({ number_of_nights: value })}
        />
      </Box>
    </Stack>
  );
}
